'use client';

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, useMemo } from 'react';
import { mergeUtmParams, type UtmParams } from './utm';

/** Target values for the underlying anchor element. */
export type LinkTarget = '_self' | '_blank' | '_parent' | '_top';

/**
 * Global link configuration provided by {@link LinkProvider}. Every value is
 * optional so a provider can override just the pieces it cares about while
 * inheriting the rest from an outer provider.
 */
export interface LinkConfig {
  /** Default UTM params applied to every link (unless overridden per-link). */
  utm?: UtmParams;
  /** Only apply UTM params to external links. Defaults to `false` (all links). */
  utmExternalOnly?: boolean;
  /** Default `target` for external links. Defaults to `_blank`. */
  externalTarget?: LinkTarget;
  /** Default `target` for internal links. Defaults to `_self`. */
  internalTarget?: LinkTarget;
  /**
   * `rel` automatically applied to external links opened in a new tab.
   * Defaults to `noopener noreferrer`. Set to `''` to disable.
   */
  externalRel?: string;
}

/** Sensible defaults used when no provider (or a partial one) is present. */
export const DEFAULT_LINK_CONFIG: Required<Omit<LinkConfig, 'utm'>> & Pick<LinkConfig, 'utm'> = {
  utm: undefined,
  utmExternalOnly: false,
  externalTarget: '_blank',
  internalTarget: '_self',
  externalRel: 'noopener noreferrer'
};
const LinkConfigContext = createContext<LinkConfig | undefined>(undefined);
export interface LinkProviderProps extends LinkConfig {
  children: React.ReactNode;
}

/**
 * Provides global defaults for the `AppLink` component. Providers can be
 * nested — an inner provider merges over the values from any outer provider,
 * so you can set app-wide UTM defaults at the root and override them for a
 * specific section (e.g. a campaign landing page).
 */
export function LinkProvider(t0) {
  const $ = _c(15);
  let children;
  let config;
  if ($[0] !== t0) {
    ({
      children,
      ...config
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = config;
  } else {
    children = $[1];
    config = $[2];
  }
  const parent = useContext(LinkConfigContext);
  const t1 = parent?.utm;
  let t2;
  if ($[3] !== config.utm || $[4] !== t1) {
    t2 = mergeUtmParams(t1, config.utm);
    $[3] = config.utm;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = config.utmExternalOnly ?? parent?.utmExternalOnly;
  const t4 = config.externalTarget ?? parent?.externalTarget;
  const t5 = config.internalTarget ?? parent?.internalTarget;
  const t6 = config.externalRel ?? parent?.externalRel;
  let t7;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4 || $[9] !== t5 || $[10] !== t6) {
    t7 = {
      utm: t2,
      utmExternalOnly: t3,
      externalTarget: t4,
      internalTarget: t5,
      externalRel: t6
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const value = t7;
  let t8;
  if ($[12] !== children || $[13] !== value) {
    t8 = <LinkConfigContext.Provider value={value}>{children}</LinkConfigContext.Provider>;
    $[12] = children;
    $[13] = value;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}

/**
 * Read the resolved link configuration, falling back to
 * {@link DEFAULT_LINK_CONFIG} for any value a provider did not set. Safe to use
 * without a surrounding {@link LinkProvider}.
 */
export function useLinkConfig() {
  const $ = _c(6);
  const config = useContext(LinkConfigContext);
  const t0 = config?.utm;
  const t1 = config?.utmExternalOnly ?? DEFAULT_LINK_CONFIG.utmExternalOnly;
  const t2 = config?.externalTarget ?? DEFAULT_LINK_CONFIG.externalTarget;
  const t3 = config?.internalTarget ?? DEFAULT_LINK_CONFIG.internalTarget;
  const t4 = config?.externalRel ?? DEFAULT_LINK_CONFIG.externalRel;
  let t5;
  if ($[0] !== t0 || $[1] !== t1 || $[2] !== t2 || $[3] !== t3 || $[4] !== t4) {
    t5 = {
      utm: t0,
      utmExternalOnly: t1,
      externalTarget: t2,
      internalTarget: t3,
      externalRel: t4
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}